import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, Table, Badge, Button, Modal, Form, Spinner, Pagination } from 'react-bootstrap';
import { FaEye, FaEyeSlash, FaEdit, FaLock, FaUserSlash, FaUserCheck } from 'react-icons/fa';
import '../styles/AdminUsuarios.css';

const ITEMS_POR_PAGINA = 15;

const AdminUsuarios = () => {
    const [usuarios, setUsuarios] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
    const [nuevaPassword, setNuevaPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [loading, setLoading] = useState(true);
    const [paginaActual, setPaginaActual] = useState(1);
    const [showModalBaja, setShowModalBaja] = useState(false);
    const [accionBaja, setAccionBaja] = useState(null); // 'baja' o 'alta'
    
    // Estados para modal de alta de empleados
    const [showModalAlta, setShowModalAlta] = useState(false);
    
    // Estados para empresas
    const [empresas, setEmpresas] = useState([]);
    
    // Estados para modal de edición de empleado
    const [showModalEdicion, setShowModalEdicion] = useState(false);
    const [empleadoEdicion, setEmpleadoEdicion] = useState(null);
    const [nuevoEmpleado, setNuevoEmpleado] = useState({
        Legajo: '',
        NmEmpleado: '',
        CUIL: '',
        CodOS: '',
        CtaBanco: '',
        CtaBancoM: '',
        CBU: '',
        IdRol: 3,
        CdEmpresa: 1
    });
    const [errorAlta, setErrorAlta] = useState('');
    
    // Verificar AdminLevel
    const [adminLevel, setAdminLevel] = useState(1);

    // Normalizar API_URL
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

    // Obtener empleados directamente con JWT
    useEffect(() => {
        console.log('🚀 useEffect ejecutándose - AdminUsuarios');
        const obtenerUsuarios = async () => {
            console.log('📞 Iniciando obtenerUsuarios()...');
            try {
                setLoading(true);
                const authToken = localStorage.getItem('token');
                const storedUser = localStorage.getItem('user');
                const user = storedUser ? JSON.parse(storedUser) : null;
                
                console.log('🔑 authToken:', authToken ? `existe (${authToken.substring(0, 20)}...)` : 'NO EXISTE');
                console.log('👤 storedUser:', storedUser ? 'existe' : 'NO EXISTE');
                console.log('👤 user parseado:', user);
                
                if (!authToken || !user) {
                    console.log('❌ DETENIDO: No hay token o user');
                    setError('No hay sesión activa');
                    setLoading(false);
                    return;
                }
                
                // Obtener AdminLevel
                const userLevel = Number(user.AdminLevel) || 1;
                setAdminLevel(userLevel);
                console.log('🔐 AdminLevel del usuario:', userLevel);

                // Obtener user ID y role de forma más robusta
                // Soporta tanto empleados (Legajo/CdEmpleado) como admins (CdUsuario)
                const userId = user.Legajo || user.CdEmpleado || user.CdUsuario || user.legajo;
                const userRole = user.IdRol || user.tipoUsuario || user.rol;

                console.log('🆔 userId extraído:', userId);
                console.log('👔 userRole extraído:', userRole);

                if (!userId) {
                    console.log('❌ DETENIDO: No se pudo extraer userId');
                    setError('Usuario sin identificador válido');
                    setLoading(false);
                    return;
                }

                console.log('🔍 Debug AdminUsuarios:');
                console.log('- authToken:', authToken ? 'existe' : 'NO EXISTE');
                console.log('- user:', user);
                console.log('- userId:', userId);
                console.log('- userRole:', userRole);
                console.log('- URL:', `${API_URL}/api/empleados`);

                const resUsuarios = await axios.get(`${API_URL}/api/empleados`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                        'x-user-id': String(userId),
                        'x-user-role': String(userRole || '1')
                    }
                });

                console.log('✅ Respuesta del servidor:', resUsuarios.data);
                console.log('📊 Total de usuarios recibidos:', resUsuarios.data.length);

                const usuariosApi = resUsuarios.data.map((emp, index) => ({
                    id: index + 1,
                    legajo: emp.Legajo,
                    nombre: emp.NmEmpleado,
                    dni: emp.CUIL || 'N/A',
                    password: emp.Clave || '****',
                    mostrarPassword: false,
                    baja: emp.Baja || 0, // 0 = activo, 1 = dado de baja
                    cdEmpresa: emp.CdEmpresa || 1,
                    nmEmpresa: emp.NmEmpresa || 'Sin asignar'
                }));

                console.log('✅ Usuarios procesados:', usuariosApi.length);
                console.log('📋 Primeros 3 usuarios:', usuariosApi.slice(0, 3));

                setUsuarios(usuariosApi);
            } catch (err) {
                console.error('❌ Error al obtener datos:', err);
                console.error('❌ Error response:', err.response);
                console.error('❌ Error status:', err.response?.status);
                console.error('❌ Error data:', err.response?.data);
                setError('Error al cargar los usuarios');
            } finally {
                setLoading(false);
            }
        };

        obtenerUsuarios();
    }, [API_URL]);

    // Cargar empresas
    useEffect(() => {
        const cargarEmpresas = async () => {
            try {
                const authToken = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/api/empresas`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    }
                });
                setEmpresas(response.data);
                console.log('✅ Empresas cargadas:', response.data);
            } catch (error) {
                console.error('❌ Error al cargar empresas:', error);
                // Si falla, usar empresa por defecto
                setEmpresas([{ CdEmpresa: 1, NmEmpresa: 'Sin asignar' }]);
            }
        };

        cargarEmpresas();
    }, [API_URL]);

    const toggleMostrarPassword = (id) => {
        setUsuarios(usuarios.map(usuario =>
            usuario.id === id
                ? { ...usuario, mostrarPassword: !usuario.mostrarPassword }
                : usuario
        ));
    };

    const handleAbrirModalCambio = (usuario) => {
        setUsuarioSeleccionado(usuario);
        setNuevaPassword('');
        setConfirmPassword('');
        setError('');
        setShowModal(true);
    };

    const handleCambiarPassword = async () => {
        if (!/^\d+$/.test(nuevaPassword)) {
            setError('La contraseña debe contener solo números');
            return;
        }

        if (nuevaPassword.length < 4) {
            setError('La contraseña debe tener al menos 4 dígitos');
            return;
        }

        if (nuevaPassword !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        try {
            const authToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            const user = storedUser ? JSON.parse(storedUser) : null;
            
            const payload = {
                usuario: usuarioSeleccionado.legajo.toString(),
                clave: nuevaPassword.toString()
            };

            await axios.post(`${API_URL}/api/auth/cambiar-password`, payload, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                    'x-user-id': String(user?.Legajo || user?.CdEmpleado || user?.CdUsuario || ''),
                    'x-user-role': String(user?.IdRol || user?.tipoUsuario || '1')
                }
            });

            setUsuarios(prev =>
                prev.map(u =>
                    u.legajo === usuarioSeleccionado.legajo
                        ? { ...u, password: nuevaPassword }
                        : u
                )
            );

            setShowModal(false);
            setUsuarioSeleccionado(null);
            setNuevaPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error('Error al cambiar contraseña:', err);
            setError('No se pudo cambiar la contraseña');
        }
    };

    const handleAbrirModalBaja = (usuario, accion) => {
        setUsuarioSeleccionado(usuario);
        setAccionBaja(accion);
        setShowModalBaja(true);
    };

    const handleCambiarEstado = async () => {
        try {
            const authToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            const user = storedUser ? JSON.parse(storedUser) : null;
            
            const nuevoEstado = accionBaja === 'baja' ? 1 : 0;
            
            await axios.put(
                `${API_URL}/api/empleados/${usuarioSeleccionado.legajo}/baja`,
                { baja: nuevoEstado },
                {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                        'x-user-id': String(user?.Legajo || user?.CdEmpleado || user?.CdUsuario || ''),
                        'x-user-role': String(user?.IdRol || user?.tipoUsuario || '1')
                    }
                }
            );

            // Actualizar el estado local
            setUsuarios(prev =>
                prev.map(u =>
                    u.legajo === usuarioSeleccionado.legajo
                        ? { ...u, baja: nuevoEstado }
                        : u
                )
            );

            setShowModalBaja(false);
            setUsuarioSeleccionado(null);
            setAccionBaja(null);
        } catch (err) {
            console.error('Error al cambiar estado:', err);
            alert('No se pudo cambiar el estado del usuario');
        }
    };

    // Handlers para modal de alta
    const handleAbrirModalAlta = () => {
        setNuevoEmpleado({
            Legajo: '',
            NmEmpleado: '',
            CUIL: '',
            CodOS: '',
            CtaBanco: '',
            CtaBancoM: '',
            CBU: '',
            IdRol: 3,
            CdEmpresa: empresas.length > 0 ? empresas[0].CdEmpresa : 1
        });
        setErrorAlta('');
        setShowModalAlta(true);
    };

    const handleCambioAlta = (campo, valor) => {
        setNuevoEmpleado(prev => ({
            ...prev,
            [campo]: valor
        }));
    };

    const validarCUIL = (cuil) => {
        const cuilLimpio = cuil.replace(/[^0-9]/g, '');
        return cuilLimpio.length >= 10;
    };

    // Handlers para modal de edición de empleado
    const handleAbrirModalEdicion = async (usuario) => {
        try {
            const authToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            const user = storedUser ? JSON.parse(storedUser) : null;
            
            // Obtener los datos completos del empleado
            const response = await axios.get(`${API_URL}/api/empleados/${usuario.legajo}`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                    'x-user-id': String(user?.Legajo || user?.CdEmpleado || user?.CdUsuario || ''),
                    'x-user-role': String(user?.IdRol || user?.tipoUsuario || '1')
                }
            });

            setEmpleadoEdicion(response.data);
            setShowModalEdicion(true);
        } catch (err) {
            console.error('Error al obtener datos del empleado:', err);
            alert('No se pudo cargar la información del empleado');
        }
    };

    const handleCambioEdicion = (campo, valor) => {
        setEmpleadoEdicion(prev => ({
            ...prev,
            [campo]: valor
        }));
    };

    const handleGuardarEdicion = async () => {
        try {
            const authToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            const user = storedUser ? JSON.parse(storedUser) : null;

            await axios.put(
                `${API_URL}/api/empleados/${empleadoEdicion.Legajo}`,
                empleadoEdicion,
                {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                        'x-user-id': String(user?.Legajo || user?.CdEmpleado || user?.CdUsuario || ''),
                        'x-user-role': String(user?.IdRol || user?.tipoUsuario || '1')
                    }
                }
            );

            // Actualizar el estado local
            const empresaSeleccionada = empresas.find(e => e.CdEmpresa === empleadoEdicion.CdEmpresa);
            setUsuarios(prev =>
                prev.map(u =>
                    u.legajo === empleadoEdicion.Legajo
                        ? { 
                            ...u, 
                            nombre: empleadoEdicion.NmEmpleado,
                            dni: empleadoEdicion.CUIL,
                            cdEmpresa: empleadoEdicion.CdEmpresa,
                            nmEmpresa: empresaSeleccionada?.NmEmpresa || 'Sin asignar'
                        }
                        : u
                )
            );

            setShowModalEdicion(false);
            setEmpleadoEdicion(null);
            alert('Datos actualizados correctamente');
        } catch (err) {
            console.error('Error al guardar datos:', err);
            alert('No se pudieron guardar los cambios');
        }
    };

    const handleCrearEmpleado = async () => {
        setErrorAlta('');

        // Validaciones
        if (!nuevoEmpleado.Legajo || !nuevoEmpleado.NmEmpleado || !nuevoEmpleado.CUIL) {
            setErrorAlta('Legajo, Nombre y CUIL son obligatorios');
            return;
        }

        if (!validarCUIL(nuevoEmpleado.CUIL)) {
            setErrorAlta('El CUIL debe tener al menos 10 dígitos');
            return;
        }

        if (!/^\d+$/.test(nuevoEmpleado.Legajo)) {
            setErrorAlta('El legajo debe contener solo números');
            return;
        }

        try {
            const authToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            const user = storedUser ? JSON.parse(storedUser) : null;

            const response = await axios.post(
                `${API_URL}/api/empleados`,
                nuevoEmpleado,
                {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                        'x-user-id': String(user?.Legajo || user?.CdEmpleado || user?.CdUsuario || ''),
                        'x-user-role': String(user?.IdRol || user?.tipoUsuario || '1')
                    }
                }
            );

            // Agregar el nuevo empleado al estado
            const empresaSeleccionada = empresas.find(e => e.CdEmpresa === nuevoEmpleado.CdEmpresa);
            const nuevoUsuario = {
                id: usuarios.length + 1,
                legajo: nuevoEmpleado.Legajo,
                nombre: nuevoEmpleado.NmEmpleado,
                dni: nuevoEmpleado.CUIL,
                password: response.data.empleado?.clave || '****',
                mostrarPassword: false,
                baja: 0,
                cdEmpresa: nuevoEmpleado.CdEmpresa,
                nmEmpresa: empresaSeleccionada?.NmEmpresa || 'Sin asignar'
            };

            setUsuarios(prev => [...prev, nuevoUsuario]);
            setShowModalAlta(false);
            alert('Empleado creado correctamente');
        } catch (err) {
            console.error('Error al crear empleado:', err);
            const errorMsg = err.response?.data?.error || 'No se pudo crear el empleado';
            setErrorAlta(errorMsg);
        }
    };

    return (
        <>
            <Card className="main-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>Administración de Colaboradores</h5>
                    <div className="d-flex gap-2 align-items-center">
                        {adminLevel >= 3 && (
                            <Button 
                                variant="success" 
                                size="sm"
                                onClick={handleAbrirModalAlta}
                            >
                                <FaUserCheck className="me-1" /> Dar de Alta
                            </Button>
                        )}
                        <Badge bg="success" pill className="me-2">
                            Activos: {usuarios.filter(u => u.baja === 0).length} colaboradores
                        </Badge>
                        <Badge bg="danger" pill>
                            Baja: {usuarios.filter(u => u.baja === 1).length} colaboradores
                        </Badge>
                    </div>
                </Card.Header>
                <div className="mb-4 px-3">
                    <Form>
                        <Form.Group controlId="busquedaUsuario">
                            <Form.Label className="fw-semibold">
                                🔍 Buscar Empleado
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Buscar por nombre, legajo o CUIL/DNI..."
                                value={busqueda}
                                onChange={(e) => {
                                    setBusqueda(e.target.value);
                                    setPaginaActual(1); // Reset a página 1 cuando busca
                                }}
                                className="shadow-sm"
                                style={{ maxWidth: '400px' }}
                            />
                            <Form.Text className="text-muted">
                                Puede buscar por nombre completo o parcial, número de legajo, o CUIL/DNI
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </div>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" role="status" variant="primary">
                                <span className="visually-hidden">Cargando...</span>
                            </Spinner>
                            <p className="mt-3">Cargando Colaboradores...</p>
                        </div>
                    ) : (() => {
                        // Filtrado mejorado: busca por nombre, legajo o CUIL
                        const usuariosFiltrados = usuarios.filter((u) => {
                            const termino = busqueda.trim().toLowerCase();
                            if (!termino) return true;
                            
                            return (
                                u.legajo.toString().includes(termino) ||
                                u.nombre.toLowerCase().includes(termino) ||
                                (u.dni && u.dni.toString().includes(termino))
                            );
                        });
                        const totalPaginas = Math.ceil(usuariosFiltrados.length / ITEMS_POR_PAGINA);
                        const inicio = (paginaActual - 1) * ITEMS_POR_PAGINA;
                        const usuariosPaginados = usuariosFiltrados.slice(inicio, inicio + ITEMS_POR_PAGINA);

                        return (
                            <>
                                <Table hover responsive className="admin-usuarios-table">
                                    <thead>
                                        <tr>
                                            <th className="col-legajo">Legajo</th>
                                            <th className="col-nombre">Nombre</th>
                                            <th className="col-cuil">CUIL</th>
                                            <th className="col-empresa">Empresa</th>
                                            <th className="col-estado">Estado</th>
                                            <th className="col-password">Contraseña</th>
                                            <th className="col-acciones">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usuariosPaginados.map((usuario) => (
                                            <tr key={usuario.id} className={usuario.baja === 1 ? 'table-secondary' : ''}>
                                                <td>{usuario.legajo}</td>
                                                <td>{usuario.nombre}</td>
                                                <td>{usuario.dni}</td>
                                                <td>
                                                    <Badge bg="info">{usuario.nmEmpresa}</Badge>
                                                </td>
                                                <td>
                                                    {usuario.baja === 1 ? (
                                                        <Badge bg="danger">Inactivo</Badge>
                                                    ) : (
                                                        <Badge bg="success">Activo</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="password-field">
                                                        {usuario.mostrarPassword ? (
                                                            <span className="text-mono">{usuario.password}</span>
                                                        ) : (
                                                            <span className="text-mono">••••••••</span>
                                                        )}
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() => toggleMostrarPassword(usuario.id)}
                                                            title={usuario.mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                                        >
                                                            {usuario.mostrarPassword ? <FaEyeSlash /> : <FaEye />}
                                                        </Button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="action-buttons-container">
                                                        {adminLevel >= 3 && (
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                className="action-btn"
                                                                onClick={() => handleAbrirModalEdicion(usuario)}
                                                                title="Editar información del empleado"
                                                            >
                                                                <FaEdit /> <span className="btn-text">Editar</span>
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="action-btn"
                                                            onClick={() => handleAbrirModalCambio(usuario)}
                                                            title="Cambiar contraseña"
                                                        >
                                                            <FaLock /> <span className="btn-text">Clave</span>
                                                        </Button>
                                                        {adminLevel >= 3 && (
                                                            <>
                                                                {usuario.baja === 1 ? (
                                                                    <Button
                                                                        variant="outline-success"
                                                                        size="sm"
                                                                        className="action-btn"
                                                                        onClick={() => handleAbrirModalBaja(usuario, 'alta')}
                                                                        title="Dar de alta"
                                                                    >
                                                                        <FaUserCheck /> <span className="btn-text">Activar</span>
                                                                    </Button>
                                                                ) : (
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        className="action-btn"
                                                                        onClick={() => handleAbrirModalBaja(usuario, 'baja')}
                                                                        title="Dar de baja"
                                                                    >
                                                                        <FaUserSlash /> <span className="btn-text">Baja</span>
                                                                    </Button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>

                                {/* Paginación */}
                                {totalPaginas > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <span className="text-muted small">
                                            Mostrando {inicio + 1} - {Math.min(inicio + ITEMS_POR_PAGINA, usuariosFiltrados.length)} de {usuariosFiltrados.length}
                                        </span>
                                        <Pagination className="mb-0">
                                            <Pagination.First 
                                                onClick={() => setPaginaActual(1)} 
                                                disabled={paginaActual === 1} 
                                            />
                                            <Pagination.Prev 
                                                onClick={() => setPaginaActual(p => Math.max(1, p - 1))} 
                                                disabled={paginaActual === 1} 
                                            />
                                            
                                            {[...Array(Math.min(5, totalPaginas))].map((_, i) => {
                                                let pageNum;
                                                if (totalPaginas <= 5) {
                                                    pageNum = i + 1;
                                                } else if (paginaActual <= 3) {
                                                    pageNum = i + 1;
                                                } else if (paginaActual >= totalPaginas - 2) {
                                                    pageNum = totalPaginas - 4 + i;
                                                } else {
                                                    pageNum = paginaActual - 2 + i;
                                                }
                                                
                                                return (
                                                    <Pagination.Item
                                                        key={pageNum}
                                                        active={pageNum === paginaActual}
                                                        onClick={() => setPaginaActual(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </Pagination.Item>
                                                );
                                            })}
                                            
                                            <Pagination.Next 
                                                onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} 
                                                disabled={paginaActual === totalPaginas} 
                                            />
                                            <Pagination.Last 
                                                onClick={() => setPaginaActual(totalPaginas)} 
                                                disabled={paginaActual === totalPaginas} 
                                            />
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaLock className="me-2" />
                        Cambiar contraseña
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Cambiando contraseña para: <strong>{usuarioSeleccionado?.nombre}</strong>
                    </p>

                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Nueva contraseña</Form.Label>
                            <Form.Control
                                type="password"
                                value={nuevaPassword}
                                onChange={(e) => setNuevaPassword(e.target.value)}
                                placeholder="Mínimo 4 caracteres"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Confirmar contraseña</Form.Label>
                            <Form.Control
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repita la nueva contraseña"
                            />
                        </Form.Group>

                        {error && <div className="alert alert-danger">{error}</div>}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleCambiarPassword}>
                        Confirmar Cambio
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmación de baja/alta */}
            <Modal show={showModalBaja} onHide={() => setShowModalBaja(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {accionBaja === 'baja' ? <FaUserSlash className="me-2 text-danger" /> : <FaUserCheck className="me-2 text-success" />}
                        {accionBaja === 'baja' ? 'Dar de baja usuario' : 'Activar usuario'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        ¿Está seguro que desea {accionBaja === 'baja' ? 'dar de baja' : 'activar'} al usuario:
                    </p>
                    <p className="fw-bold">
                        {usuarioSeleccionado?.nombre} (Legajo: {usuarioSeleccionado?.legajo})
                    </p>
                    {accionBaja === 'baja' && (
                        <div className="alert alert-warning">
                            <small>
                                El usuario no podrá iniciar sesión hasta que sea activado nuevamente.
                            </small>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalBaja(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        variant={accionBaja === 'baja' ? 'danger' : 'success'} 
                        onClick={handleCambiarEstado}
                    >
                        Confirmar {accionBaja === 'baja' ? 'Baja' : 'Activación'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de alta de empleado */}
            <Modal show={showModalAlta} onHide={() => setShowModalAlta(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaUserCheck className="me-2 text-success" />
                        Dar de Alta Nuevo Empleado
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Legajo <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={nuevoEmpleado.Legajo}
                                        onChange={(e) => handleCambioAlta('Legajo', e.target.value)}
                                        placeholder="Ej: 12345"
                                    />
                                    <Form.Text className="text-muted">
                                        Solo números. El CdEmpleado será igual al Legajo.
                                    </Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>CUIL <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={nuevoEmpleado.CUIL}
                                        onChange={(e) => handleCambioAlta('CUIL', e.target.value)}
                                        placeholder="20-12345678-9"
                                    />
                                    <Form.Text className="text-muted">
                                        La clave serán los últimos 4 dígitos del DNI.
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Nombre Completo <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={nuevoEmpleado.NmEmpleado}
                                onChange={(e) => handleCambioAlta('NmEmpleado', e.target.value)}
                                placeholder="Apellido, Nombre"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Rol</Form.Label>
                            <Form.Select
                                value={nuevoEmpleado.IdRol}
                                onChange={(e) => handleCambioAlta('IdRol', parseInt(e.target.value))}
                            >
                                <option value={1}>1 - Administración</option>
                                <option value={2}>2 - Inspección</option>
                                <option value={3}>3 - Taller</option>
                                <option value={4}>4 - Conducción</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Empresa <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                value={nuevoEmpleado.CdEmpresa}
                                onChange={(e) => handleCambioAlta('CdEmpresa', parseInt(e.target.value))}
                            >
                                {empresas.length === 0 ? (
                                    <option value={1}>Cargando empresas...</option>
                                ) : (
                                    empresas.map(emp => (
                                        <option key={emp.CdEmpresa} value={emp.CdEmpresa}>
                                            {emp.CdEmpresa} - {emp.NmEmpresa}
                                        </option>
                                    ))
                                )}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Seleccione la empresa a la que pertenece el empleado
                            </Form.Text>
                        </Form.Group>

                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Código Obra Social</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={nuevoEmpleado.CodOS}
                                        onChange={(e) => handleCambioAlta('CodOS', e.target.value)}
                                        placeholder="Opcional"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>CBU</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={nuevoEmpleado.CBU}
                                        onChange={(e) => handleCambioAlta('CBU', e.target.value)}
                                        placeholder="Opcional (22 dígitos)"
                                        maxLength={22}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Cuenta Banco</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={nuevoEmpleado.CtaBanco}
                                        onChange={(e) => handleCambioAlta('CtaBanco', e.target.value)}
                                        placeholder="Opcional"
                                        maxLength={12}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Cuenta Banco M</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={nuevoEmpleado.CtaBancoM}
                                        onChange={(e) => handleCambioAlta('CtaBancoM', e.target.value)}
                                        placeholder="Opcional"
                                        maxLength={15}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="alert alert-info">
                            <small>
                                <strong>Nota:</strong> Los campos marcados con <span className="text-danger">*</span> son obligatorios.
                                La contraseña inicial serán los últimos 4 dígitos del DNI extraído del CUIL.
                            </small>
                        </div>

                        {errorAlta && <div className="alert alert-danger">{errorAlta}</div>}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalAlta(false)}>
                        Cancelar
                    </Button>
                    <Button variant="success" onClick={handleCrearEmpleado}>
                        <FaUserCheck className="me-1" /> Crear Empleado
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de edición de empleado */}
            <Modal show={showModalEdicion} onHide={() => setShowModalEdicion(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaEdit className="me-2 text-info" />
                        Editar Empleado
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {empleadoEdicion && (
                        <Form>
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Legajo</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={empleadoEdicion.Legajo}
                                            disabled
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            El legajo no se puede modificar
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>CUIL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={empleadoEdicion.CUIL || ''}
                                            onChange={(e) => handleCambioEdicion('CUIL', e.target.value)}
                                            placeholder="20-12345678-9"
                                        />
                                    </Form.Group>
                                </div>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Nombre Completo</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={empleadoEdicion.NmEmpleado || ''}
                                    onChange={(e) => handleCambioEdicion('NmEmpleado', e.target.value)}
                                    placeholder="Apellido, Nombre"
                                />
                            </Form.Group>

                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>CBU</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={empleadoEdicion.CBU || ''}
                                            onChange={(e) => handleCambioEdicion('CBU', e.target.value)}
                                            placeholder="22 dígitos"
                                            maxLength={22}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Cuenta Banco</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={empleadoEdicion.CtaBanco || ''}
                                            onChange={(e) => handleCambioEdicion('CtaBanco', e.target.value)}
                                            placeholder="Hasta 12 caracteres"
                                            maxLength={12}
                                        />
                                    </Form.Group>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Cuenta Banco M</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={empleadoEdicion.CtaBancoM || ''}
                                            onChange={(e) => handleCambioEdicion('CtaBancoM', e.target.value)}
                                            placeholder="Hasta 15 caracteres"
                                            maxLength={15}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Código Obra Social</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={empleadoEdicion.CodOS || ''}
                                            onChange={(e) => handleCambioEdicion('CodOS', e.target.value)}
                                            placeholder="Código de OS"
                                        />
                                    </Form.Group>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Esposa</Form.Label>
                                        <Form.Select
                                            value={empleadoEdicion.Esposa || 0}
                                            onChange={(e) => handleCambioEdicion('Esposa', parseInt(e.target.value))}
                                        >
                                            <option value={0}>No</option>
                                            <option value={1}>Sí</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Hijos</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={empleadoEdicion.Hijos || 0}
                                            onChange={(e) => handleCambioEdicion('Hijos', parseInt(e.target.value) || 0)}
                                            min={0}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>AdOS</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={empleadoEdicion.AdOS || 0}
                                            onChange={(e) => handleCambioEdicion('AdOS', parseInt(e.target.value) || 0)}
                                            min={0}
                                        />
                                    </Form.Group>
                                </div>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Rol</Form.Label>
                                <Form.Select
                                    value={empleadoEdicion.IdRol || 3}
                                    onChange={(e) => handleCambioEdicion('IdRol', parseInt(e.target.value))}
                                >
                                    <option value={1}>1 - Administración</option>
                                    <option value={2}>2 - Inspección</option>
                                    <option value={3}>3 - Taller</option>
                                    <option value={4}>4 - Conducción</option>
                                    <option value={99}>99 - Super Admin</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Empresa</Form.Label>
                                <Form.Select
                                    value={empleadoEdicion.CdEmpresa || 1}
                                    onChange={(e) => handleCambioEdicion('CdEmpresa', parseInt(e.target.value))}
                                >
                                    {empresas.length > 0 ? (
                                        empresas.map(empresa => (
                                            <option key={empresa.CdEmpresa} value={empresa.CdEmpresa}>
                                                {empresa.CdEmpresa} - {empresa.NmEmpresa}
                                            </option>
                                        ))
                                    ) : (
                                        <option value={1}>1 - Sin asignar</option>
                                    )}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Seleccione a qué empresa pertenece el colaborador
                                </Form.Text>
                            </Form.Group>

                            <div className="alert alert-info">
                                <small>
                                    <strong>Nota:</strong> Solo modifique los campos que desea actualizar. 
                                    Los campos vacíos se mantendrán sin cambios.
                                </small>
                            </div>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalEdicion(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleGuardarEdicion}>
                        <FaEdit className="me-1" /> Guardar Cambios
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdminUsuarios;